import { useState, useEffect, createContext, useContext } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical tier hierarchy — lowest to highest.
// Any allow-list that contains `pro` implicitly allows all higher tiers.
export const TIER_ORDER = ['basic', 'starter', 'pro', 'advanced', 'enterprise'];

/** Return true if `tier` meets or exceeds the `minimum` tier. */
export function tierMeets(tier, minimum) {
  const t = TIER_ORDER.indexOf((tier || 'basic').toLowerCase());
  const m = TIER_ORDER.indexOf((minimum || 'basic').toLowerCase());
  if (t < 0 || m < 0) return false;
  return t >= m;
}

const TierContext = createContext({ tier: 'enterprise', loading: true });

export function TierProvider({ children }) {
  const [tier, setTier] = useState('enterprise');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/user/tier`).then(r => { setTier(r.data.tier); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return <TierContext.Provider value={{ tier, setTier, loading }}>{children}</TierContext.Provider>;
}

export function useTier() { return useContext(TierContext); }

/**
 * Tier gate.
 *  - Default (no props): enterprise-only.
 *  - Pass `minTier="pro"` for a hierarchical check — Pro and above pass.
 *  - Pass `allowedTiers={['starter','advanced']}` for explicit allow-list.
 */
export function EnterpriseGate({ children, allowedTiers, minTier }) {
  const { tier, loading } = useTier();
  let allow;
  if (minTier) {
    const idx = TIER_ORDER.indexOf(minTier.toLowerCase());
    allow = idx >= 0 ? TIER_ORDER.slice(idx) : ['enterprise'];
  } else {
    allow = allowedTiers && allowedTiers.length ? allowedTiers : ['enterprise'];
  }

  if (loading) return null;
  if (allow.includes(tier)) return children;

  return (
    <div className="flex items-center justify-center min-h-[50vh]" data-testid="enterprise-gate">
      <Card className="p-8 max-w-md text-center animate-scale-in">
        <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
          <Crown className="w-7 h-7 text-amber-500" />
        </div>
        <h2 className="text-xl font-heading font-bold mb-2">Upgrade required</h2>
        <p className="text-sm text-muted-foreground mb-5">
          This feature is available on the <b>{allow.join(' / ')}</b> plan{allow.length > 1 ? 's' : ''}.
          Your current plan is <b>{tier}</b>.
        </p>
        <Link to="/pricing">
          <Button className="bg-primary hover:bg-primary/90" data-testid="upgrade-btn">
            View plans <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </Card>
    </div>
  );
}
